import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { admins, currentSeason } from "../../Constants";
import {
    TournamentConfig,
    TournamentTestResult,
    getAllTournamentConfigs,
    addTournamentConfig,
    updateTournamentConfig,
    deleteTournamentConfig,
    testTournamentFetch,
} from "../../services/TournamentService";
import styles from "./TournamentAdminPage.module.css";

const TournamentAdminPage: React.FC = () => {
    const { user, token } = useContext(AuthContext);
    const [configs, setConfigs] = useState<TournamentConfig[]>([]);
    const [password, setPassword] = useState("");
    const [authenticated, setAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [formTournamentId, setFormTournamentId] = useState("");
    const [formSeason, setFormSeason] = useState(currentSeason);
    const [formDivisionName, setFormDivisionName] = useState("");
    const [formDisplayName, setFormDisplayName] = useState("");
    const [formCountsTowardStats, setFormCountsTowardStats] = useState(false);
    const [formQualifiedTeamIds, setFormQualifiedTeamIds] = useState("");
    const [formSubmitting, setFormSubmitting] = useState(false);

    const [testResult, setTestResult] = useState<TournamentTestResult | null>(null);
    const [testLoading, setTestLoading] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);

    const isAdmin = user && admins.includes(user.steamID);

    useEffect(() => { loadConfigs(); }, []);

    const loadConfigs = async () => {
        setLoading(true);
        try {
            setConfigs(await getAllTournamentConfigs());
        } catch { setError("Failed to load configs."); }
        setLoading(false);
    };

    // The server checks the password on every call; this only unlocks the form.
    const handleAuth = () => {
        if (password.trim()) { setAuthenticated(true); setError(""); }
        else { setError("Enter the admin password."); }
    };

    const handleTest = async () => {
        if (!formTournamentId) return;
        setTestLoading(true);
        setTestResult(null);
        try {
            setTestResult(await testTournamentFetch(formTournamentId, password, token!));
        } catch { setTestResult({ success: false, message: "Fetch failed." }); }
        setTestLoading(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formTournamentId || !formDivisionName || !formDisplayName) {
            setError("All fields are required."); return;
        }
        setFormSubmitting(true); setError("");
        try {
            const payload = {
                tournamentId: formTournamentId, season: formSeason,
                divisionName: formDivisionName, displayName: formDisplayName,
                isActive: true, countsTowardStats: formCountsTowardStats,
                qualifiedTeamIds: formQualifiedTeamIds || null,
            };
            if (editingId) await updateTournamentConfig(editingId, payload, password, token!);
            else await addTournamentConfig(payload, password, token!);
            resetForm(); await loadConfigs();
        } catch (err: any) { setError(err.response?.data || "Failed to save."); }
        setFormSubmitting(false);
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Delete this tournament config?")) return;
        try { await deleteTournamentConfig(id, password, token!); await loadConfigs(); }
        catch { setError("Failed to delete."); }
    };

    const handleToggleActive = async (config: TournamentConfig) => {
        try {
            await updateTournamentConfig(config.id, { ...config, isActive: !config.isActive }, password, token!);
            await loadConfigs();
        } catch { setError("Failed to toggle."); }
    };

    const handleEdit = (c: TournamentConfig) => {
        setEditingId(c.id); setFormTournamentId(c.tournamentId);
        setFormSeason(c.season); setFormDivisionName(c.divisionName);
        setFormDisplayName(c.displayName); setFormCountsTowardStats(c.countsTowardStats);
        setFormQualifiedTeamIds(c.qualifiedTeamIds || "");
    };

    const resetForm = () => {
        setEditingId(null); setFormTournamentId(""); setFormSeason(currentSeason);
        setFormDivisionName(""); setFormDisplayName("");
        setFormCountsTowardStats(false); setFormQualifiedTeamIds(""); setTestResult(null);
    };

    if (!isAdmin) {
        return <div className={styles.denied}><h2>Access Denied</h2><p>Admin only.</p></div>;
    }

    if (!authenticated) {
        return (
            <div className={styles.authContainer}>
                <h2 className={styles.authTitle}>Tournament Admin</h2>
                <input
                    type="password" className={styles.authInput} placeholder="Admin password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAuth()}
                />
                {error && <div className={styles.error}>{error}</div>}
                <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleAuth}>Login</button>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>Tournaments</h1>
            <p className={styles.subtitle}>Manage OverPass tournament IDs mapped to season/division pairs.</p>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.formCard}>
                <div className={styles.formTitle}>{editingId ? "Edit Tournament" : "Add Tournament"}</div>
                <form onSubmit={handleSubmit}>
                    <div className={styles.formGrid}>
                        <div>
                            <div className={styles.fieldLabel}>Tournament ID</div>
                            <div className={styles.inputGroup}>
                                <input className={styles.input} value={formTournamentId}
                                    onChange={(e) => setFormTournamentId(e.target.value)}
                                    placeholder="e.g. 2446053782879803391" />
                                <button type="button" className={styles.btnTest} onClick={handleTest}
                                    disabled={testLoading || !formTournamentId}>
                                    {testLoading ? "..." : "Test"}
                                </button>
                            </div>
                        </div>
                        <div>
                            <div className={styles.fieldLabel}>Season</div>
                            <input type="number" className={styles.input} value={formSeason}
                                onChange={(e) => setFormSeason(parseInt(e.target.value) || 0)} />
                        </div>
                        <div>
                            <div className={styles.fieldLabel}>Division Name</div>
                            <input className={styles.input} value={formDivisionName}
                                onChange={(e) => setFormDivisionName(e.target.value)}
                                placeholder="e.g. liga-kval-1" />
                        </div>
                    </div>
                    <div className={styles.formRow}>
                        <div>
                            <div className={styles.fieldLabel}>Display Name</div>
                            <input className={styles.input} value={formDisplayName}
                                onChange={(e) => setFormDisplayName(e.target.value)}
                                placeholder="e.g. Liga Kvalifikation 1" />
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-end" }}>
                            <label className={styles.checkbox}>
                                <input type="checkbox" checked={formCountsTowardStats}
                                    onChange={(e) => setFormCountsTowardStats(e.target.checked)} />
                                <span>Counts toward season stats</span>
                            </label>
                        </div>
                    </div>
                    {/* Qualified teams picker - show after test or when editing */}
                    {(testResult?.teams || formQualifiedTeamIds) && (
                        <div style={{ marginBottom: 12 }}>
                            <div className={styles.fieldLabel}>Qualified Teams (click to toggle)</div>
                            {testResult?.teams ? (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                                    {testResult.teams.map(t => {
                                        const selectedIds = formQualifiedTeamIds.split(",").filter(Boolean).map(s => s.trim());
                                        const isSelected = selectedIds.includes(t.id);
                                        return (
                                            <button
                                                key={t.id} type="button"
                                                onClick={() => {
                                                    if (isSelected) {
                                                        setFormQualifiedTeamIds(selectedIds.filter(id => id !== t.id).join(","));
                                                    } else {
                                                        setFormQualifiedTeamIds([...selectedIds, t.id].join(","));
                                                    }
                                                }}
                                                style={{
                                                    display: "flex", alignItems: "center", gap: 6,
                                                    padding: "6px 12px", borderRadius: 6, cursor: "pointer",
                                                    border: isSelected ? "1px solid #4ade80" : "1px solid rgba(255,255,255,0.1)",
                                                    background: isSelected ? "rgba(74,222,128,0.15)" : "rgba(255,255,255,0.03)",
                                                    color: isSelected ? "#4ade80" : "rgba(255,255,255,0.6)",
                                                    transition: "all 0.2s", fontSize: "0.85rem"
                                                }}
                                            >
                                                {t.logo && <img src={t.logo} alt="" style={{ width: 18, height: 18, objectFit: "contain" }} />}
                                                {t.name}
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.85rem", marginTop: 4 }}>
                                    Click "Test" to load teams for selection
                                </div>
                            )}
                            {formQualifiedTeamIds && (
                                <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.75rem", marginTop: 4 }}>
                                    {formQualifiedTeamIds.split(",").filter(Boolean).length} team(s) selected
                                </div>
                            )}
                        </div>
                    )}
                    <div className={styles.formActions}>
                        <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={formSubmitting}>
                            {formSubmitting ? "Saving..." : editingId ? "Update" : "Add"}
                        </button>
                        {editingId && (
                            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={resetForm}>Cancel</button>
                        )}
                    </div>
                </form>

                {testResult && (
                    <div className={styles.testResult}>
                        {testResult.success ? (
                            <>
                                <div className={styles.testSuccess}>{testResult.name}</div>
                                <div className={styles.testInfo}>
                                    {testResult.teamCount} teams &middot; {testResult.completedMatches}/{testResult.totalMatches} matches complete &middot; {testResult.matchesWithDemos} with demos
                                </div>
                            </>
                        ) : (
                            <div className={styles.testError}>{testResult.message}</div>
                        )}
                    </div>
                )}
            </div>

            {loading ? (
                <div className={styles.noData}>Loading...</div>
            ) : configs.length === 0 ? (
                <div className={styles.noData}>No tournament configs yet.</div>
            ) : (
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Tournament ID</th>
                                <th>Season</th>
                                <th>Division</th>
                                <th>Stats</th>
                                <th>Status</th>
                                <th>Qualified</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {configs.map((c) => (
                                <tr key={c.id} className={styles.row}>
                                    <td>{c.displayName}</td>
                                    <td className={styles.tournamentId}>{c.tournamentId}</td>
                                    <td>{c.season}</td>
                                    <td><span className={styles.divisionBadge}>{c.divisionName}</span></td>
                                    <td>
                                        <span className={`${styles.statsBadge} ${c.countsTowardStats ? styles.statsYes : styles.statsNo}`}>
                                            {c.countsTowardStats ? "Yes" : "No"}
                                        </span>
                                    </td>
                                    <td>
                                        <button
                                            className={`${styles.activeBadge} ${c.isActive ? styles.activeOn : styles.activeOff}`}
                                            onClick={() => handleToggleActive(c)}>
                                            {c.isActive ? "Active" : "Inactive"}
                                        </button>
                                    </td>
                                    <td style={{ color: "var(--text-muted)" }}>
                                        {c.qualifiedTeamIds ? `${c.qualifiedTeamIds.split(",").filter(Boolean).length} teams` : "—"}
                                    </td>
                                    <td>
                                        <div className={styles.actions}>
                                            <button className={`${styles.btnSmall} ${styles.btnEdit}`} onClick={() => handleEdit(c)}>Edit</button>
                                            <button className={`${styles.btnSmall} ${styles.btnDelete}`} onClick={() => handleDelete(c.id)}>Delete</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default TournamentAdminPage;
